// Attachments kept on this device (HRTH-110/113). The server is only a delivery queue: it
// deletes an attachment once every device it was for has downloaded it, or after 30
// days. So what someone downloads (or plays, or sent) is kept here, decrypted, in this
// browser's own storage, never sent anywhere.

const DB = 'hearth-attachments'
const STORE = 'files'

let opened: Promise<IDBDatabase> | null = null

function db(): Promise<IDBDatabase> {
  opened ??= new Promise((resolve, reject) => {
    const r = indexedDB.open(DB, 1)
    r.onupgradeneeded = () => r.result.createObjectStore(STORE)
    r.onsuccess = () => resolve(r.result)
    r.onerror = () => reject(r.error)
  })
  opened.catch(() => (opened = null))
  return opened
}

function run<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return db().then(
    (d) =>
      new Promise<T>((resolve, reject) => {
        const tx = d.transaction(STORE, mode)
        const r = fn(tx.objectStore(STORE))
        tx.oncomplete = () => resolve(r.result)
        tx.onerror = () => reject(tx.error)
        tx.onabort = () => reject(tx.error ?? new Error('aborted'))
      }),
  )
}

/** The attachment, if this device has it. */
export async function localAttachment(id: string): Promise<Blob | null> {
  try {
    return ((await run('readonly', (s) => s.get(id))) as Blob | undefined) ?? null
  } catch {
    return null
  }
}

export async function keepLocally(id: string, file: Blob): Promise<void> {
  // Ask the browser not to evict what we keep (granted quietly for installed apps).
  void navigator.storage?.persist?.().catch(() => false)
  await run('readwrite', (s) => s.put(file, id))
}

/** Delete from device: the kept copy goes too. */
export async function forgetAttachment(id: string): Promise<void> {
  try {
    await run('readwrite', (s) => s.delete(id))
  } catch {
    // Not kept.
  }
}

/** Signing out forgets them with everything else. */
export async function forgetAttachments(): Promise<void> {
  try {
    await run('readwrite', (s) => s.clear())
  } catch {
    // Nothing kept.
  }
}
